<script lang="ts">
export default defineComponent({
  name: "GallerySubNav",
});
</script>

<script setup lang="ts">
import { BaseButtonKindEnum } from "@/components/Base/Button";

export type GallerySubNavItem = {
  label: string;
  to: string | RouterLinks;
};

export type GallerySubNavProps = {
  items: GallerySubNavItem[];
};

defineProps<GallerySubNavProps>();

const isExternalLink = (to: string | RouterLinks): boolean => {
  return (
    typeof to === "string" &&
    (to.startsWith("http://") || to.startsWith("https://"))
  );
};
</script>

<template>
  <div class="sub-nav">
    <nav class="container">
      <div class="sub-nav__scroll-container">
        <ul class="sub-nav__list">
          <li
            v-for="item in items"
            :key="item.label"
            class="sub-nav__item-wrapper"
          >
            <BaseButton
              v-if="!isExternalLink(item.to)"
              class="sub-nav__item"
              :kind="BaseButtonKindEnum.LINK"
              :to="item.to"
            >
              {{ item.label }}
            </BaseButton>
            <a
              v-else
              :href="item.to.toString()"
              target="_blank"
              rel="noopener noreferrer"
              class="sub-nav__item"
            >
              {{ item.label }}
            </a>
          </li>
        </ul>
      </div>
    </nav>
  </div>
</template>

<style scoped lang="scss">
.sub-nav {
  background: $color-white;
  border-bottom: 1px solid $border-color;
  height: $sub-nav-height;

  &__scroll-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    &::-webkit-scrollbar {
      /* WebKit */
      width: 0;
      height: 0;
    }
  }

  &__list {
    display: inline-flex;
    flex-wrap: nowrap;
    list-style: none;
    padding: 0;
    margin: 0;
    height: $sub-nav-height;
  }

  &__item-wrapper {
    flex: 0 0 auto;
    height: $sub-nav-height;
  }

  &__item {
    position: relative;
    color: $body-color;
    display: flex;
    align-items: center;
    height: $sub-nav-height;
    padding: 0 $spacer-3;
    text-decoration: none;
    white-space: nowrap;

    @include media-breakpoint-down(md) {
      padding: 0 $spacer-2;
    }

    &::before {
      content: "";
      position: absolute;
      bottom: 0;
      left: $spacer-3;
      right: $spacer-3;
      height: 2px;
      background-color: $brand-courses;
      display: none;

      @include media-breakpoint-down(md) {
        left: $spacer-2;
        right: $spacer-2;
      }
    }

    &.active::before,
    &:hover::before {
      display: flex;
    }
  }

  .router-link-exact-active {
    font-weight: $font-weight-semibold !important;

    &::before {
      display: flex;
    }
  }
}

// Responsive adjustments
@include media-breakpoint-up(xl) {
  .sub-nav__list {
    margin-left: 126px;
  }
}

@include media-breakpoint-only(lg) {
  .sub-nav__list {
    margin-left: 118px;
  }
}

@include media-breakpoint-only(md) {
  .sub-nav__list {
    margin-left: 32px;
  }
}

@include media-breakpoint-only(sm) {
  .sub-nav__list {
    margin-left: 0;
  }
}
</style>
